import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getConversation } from '../api/conversations';
import { extractArtifacts, mapConversationMessage } from '../pages/chat/conversationMapper';
import { useChatStore } from '../stores/chatStore';

const POLL_INTERVAL_MS = 3000;
const RESUME_TIMEOUT_MS = 5 * 60 * 1000;

function lastMessageIsSmeAuthored(
  messages: ReadonlyArray<{ role: string; senderType?: string }>,
): boolean {
  if (messages.length === 0) return false;
  const last = messages[messages.length - 1]!;
  // Server-loaded rows carry senderType. Only SME messages trigger an AI run,
  // so only SME should engage resume mode. REVIEWER, AI, and system messages
  // are all terminal from the SME dashboard's perspective.
  if (last.senderType) return last.senderType === 'SME';
  // Optimistic bubbles (pre-persistence) have no senderType; role='user' means
  // the SME just sent it, which is the same signal.
  return last.role === 'user';
}

export function useChatResume(conversationId: string | null) {
  const queryClient = useQueryClient();
  // Subscribe to hydration + streaming so resume checks re-run once the store
  // finishes cold-loading the conversation. We intentionally do NOT subscribe
  // to resumeActive as a dep — the effect itself sets it to true, and a dep-
  // based re-run would tear down the interval we just armed. External clears
  // of resumeActive are observed by the poll/timeout callbacks at their next
  // tick instead.
  const hydratedForConvId = useChatStore((s) => s.hydratedForConvId);
  const isStreaming = useChatStore((s) => s.isStreaming);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!conversationId) return;
    if (hydratedForConvId !== conversationId) return;
    if (isStreaming) return;

    const store = useChatStore.getState();
    if (store.resumeActive) return;
    if (!lastMessageIsSmeAuthored(store.messages)) return;

    // Captured by async callbacks; flipped on cleanup so in-flight promises
    // don't write to the store after the effect tears down (e.g. after a
    // conversation switch).
    let cancelled = false;

    store.injectResumePlaceholder();
    store.setResumeActive(true);

    // Idempotent — safe to call multiple times.
    const stop = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const finishWithMessages = async (data: Awaited<ReturnType<typeof getConversation>>) => {
      try {
        if (cancelled) return;
        const chatMessages = (data.messages ?? []).map(mapConversationMessage);
        const artifacts = extractArtifacts(data.messages ?? []);
        const s = useChatStore.getState();
        s.clearResumePlaceholder();
        // loadConversation also resets resumeActive to false (see chatStore).
        s.loadConversation(conversationId, chatMessages, artifacts);
        queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      } finally {
        stop();
      }
    };

    const poll = async () => {
      // Bail if an external action (send, reset) already cleared resume state.
      if (!useChatStore.getState().resumeActive) {
        stop();
        return;
      }
      try {
        const data = await getConversation(conversationId);
        if (cancelled) return;
        const last = data.messages?.[data.messages.length - 1];
        if (last && last.senderType === 'AI') {
          await finishWithMessages(data);
        }
      } catch {
        // Network blip — keep polling until timeout.
      }
    };

    pollIntervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    timeoutRef.current = setTimeout(() => {
      if (cancelled) return;
      const s = useChatStore.getState();
      if (!s.resumeActive) {
        stop();
        return;
      }
      s.clearResumePlaceholder();
      s.setError('RESUME_TIMEOUT');
      s.setResumeActive(false);
      stop();
    }, RESUME_TIMEOUT_MS);

    return () => {
      cancelled = true;
      stop();
    };
  }, [conversationId, hydratedForConvId, isStreaming, queryClient]);
}
